import React, { useState, useEffect } from 'react'
import { Checkbox, Select, Button, Space, Tooltip } from 'antd'
import styled from 'styled-components'
import { SmallSpinner } from 'portal/lib/primitives'
import PropTypes from 'prop-types'
import _ from 'lodash'

// Sentinel the server uses to represent a blank/null field value, rendered as a friendly "Blank" option
export const SERVER_NULL_OR_EMPTY_SENTINEL = '(nullOrEmpty)'

// SOURCE_MAPPING : Shorter version that the Mapping found here:
// \apps\portal\src\app\components\RecordsModules\Details\WasteManagementRoutes.tsx
export const SOURCE_MAPPING = {
  Invalid: null,
  None: null,
  PermitIdEWCCode: 'Facility and EWC code regulatory data',
  PermitId: 'Facility average using regulatory data',
  PermitTypeEWCCode:
    'Site type average across all regulatory data for specified EWC code',
  PermitType: 'Site type average across all regulatory data',
  EWCCode: 'EWC Code average across all regulatory data',
  Exemption: 'Exemption',
  Unknown: 'Unable to derive',
  CustomPermitId: 'Facility average using client-defined waste route',
  CustomPermitIdEWCCode: 'Facility and EWC code client-defined waste route'
}

function dropdownRender(
  menu,
  name,
  state,
  projectId,
  hasStaticOptions,
  selected,
  handleOk,
  handleReset
) {
  // Skip loading/error checks for hardcoded SOURCE_MAPPING
  if (!hasStaticOptions && name !== 'items[].wasteManagementRoutesSource') {
    if (_.get(state.options, [projectId, name, 'loading'], true)) {
      return (
        <div style={{ textAlign: 'center' }}>
          <SmallSpinner />
        </div>
      )
    }
    if (state.options[projectId][name].error !== null) {
      return <div> An error occured while loading options.</div>
    }
  }
  return (
    <>
      {menu}
      <div style={{ padding: 8, borderTop: '1px solid #f0f0f0' }}>
        <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            size="small"
            type="text"
            disabled={selected.length === 0}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button type="primary" size="small" onClick={handleOk}>
            OK
          </Button>
        </Space>
      </div>
    </>
  )
}

export function RecordsSelect(
  name,
  state,
  fetchOptions,
  filters,
  updateFilter,
  projectId,
  tableId,
  staticOptions
) {
  const [selected, setSelected] = useState(filters?.[name] || [])

  const NULL_OR_EMPTY_LABEL = 'Blank'

  const isChildFilter = name.includes('[].')
  const itemLevelDescription = name.includes('items[]')
    ? 'Waste Item'
    : 'Product'
  const hasStaticOptions = Array.isArray(staticOptions)

  // Keep local selection in sync when persisted filters are hydrated after mount.
  useEffect(() => {
    const nextSelected = filters?.[name]
    setSelected(Array.isArray(nextSelected) ? nextSelected : [])
  }, [filters, name])

  // Pre-load options for assignedTo filter. fetchOptions (assignedToOptions) is re-created when
  // assignableUsers updates, so including it as a dep ensures we re-load once actual users arrive.
  // Calling fetchOptions only updates records.options - it does NOT change assignableUsers, so no loop.
  useEffect(() => {
    if (name === 'assignedTo') {
      fetchOptions(name)
    }
  }, [name, projectId, fetchOptions])

  // Special handling for wasteManagementRoutesSource - use SOURCE_MAPPING
  let allOptions
  if (hasStaticOptions) {
    allOptions = staticOptions
      .filter(option => option && /\S/.test(option))
      .map(option => ({ key: option, label: option }))
  } else if (name === 'items[].wasteManagementRoutesSource') {
    allOptions = Object.entries(SOURCE_MAPPING)
      .filter(([key, value]) => value !== null)
      .map(([key, value]) => ({
        key: key,
        label: value
      }))
  } else {
    allOptions = Array(
      ..._.get(state.options, [projectId, name, 'options'], [])
    )

    allOptions = allOptions.map(option =>
      option === SERVER_NULL_OR_EMPTY_SENTINEL // Give the null-or-empty sentinel from the server a friendly label
        ? {
            key: SERVER_NULL_OR_EMPTY_SENTINEL,
            value: NULL_OR_EMPTY_LABEL,
            searchText: NULL_OR_EMPTY_LABEL,
            label: isChildFilter ? (
              <Tooltip
                title={`On this ${itemLevelDescription} column, ${NULL_OR_EMPTY_LABEL} means one or more ${itemLevelDescription.toLowerCase()}s with a ${NULL_OR_EMPTY_LABEL} value`}
              >
                <i>{NULL_OR_EMPTY_LABEL}</i>
              </Tooltip>
            ) : (
              <i>{NULL_OR_EMPTY_LABEL}</i>
            )
          }
        : option
    )
  }

  // Separate special filters (e.g., "Unassigned") from regular options
  const specialFilters = allOptions.filter(
    option =>
      option.key === 'unassigned' ||
      option.key === SERVER_NULL_OR_EMPTY_SENTINEL
  )
  const regularOptions = allOptions.filter(
    option =>
      option.key !== 'unassigned' &&
      option.key !== SERVER_NULL_OR_EMPTY_SENTINEL
  )

  // Sort only the regular options
  const sortedRegularOptions = regularOptions.sort((a, b) =>
    String(a.label ?? a).localeCompare(String(b.label ?? b), 'en', {
      sensitivity: 'base'
    })
  )

  // Concatenate special filters first, then sorted regular options
  const sortedOptions = [...specialFilters, ...sortedRegularOptions]

  const dropdownOptions = []

  // Options can be an array of strings or objects with 'key' & 'label' properties
  sortedOptions.map(option =>
    dropdownOptions.push({
      key: option.key || option,
      value: option.key || option,
      label: option.label || option,
      ...(option.searchText != null && { searchText: option.searchText })
    })
  )

  const classNameForWidth =
    name === 'items[].knownContainerType'
      ? 'knownContainerType'
      : name === 'items[].wasteCode'
        ? 'wasteCode'
        : name === 'items[].wasteManagementRoutesSource'
          ? 'wasteManagementRoutesSource'
          : name === 'products[].productGroupName'
            ? 'productGroupNames'
            : name

  const width =
    document.querySelector(`th.${classNameForWidth}`)?.offsetWidth || 200

  const handleReset = () => {
    setSelected([]) // clear temporary selection
  }

  const handleOk = () => {
    // Update filter with the current selection (which will cause a reload of the table data)
    updateFilter(name, selected)
  }

  return (
    <div style={{ width: width }}>
      <Select
        className="records-filter-select"
        name={name}
        data-testid={`${name}Filter`}
        autoClearSearchValue={false}
        maxTagCount="responsive"
        popupMatchSelectWidth={false}
        mode="multiple"
        showSearch
        allowClear
        style={{ width: '100%' }}
        options={dropdownOptions}
        optionRender={option => {
          const labelText =
            typeof option.label === 'string'
              ? option.label
              : option.searchText || option.value
          return (
            <Checkbox checked={selected.includes(option.value)}>
              <Tooltip title={labelText} mouseEnterDelay={0.5}>
                <TruncatedLabel>{option.label}</TruncatedLabel>
              </Tooltip>
            </Checkbox>
          )
        }}
        menuItemSelectedIcon={null}
        popupRender={menu =>
          dropdownRender(
            menu,
            name,
            state,
            projectId,
            hasStaticOptions,
            selected,
            handleOk,
            handleReset
          )
        }
        filterOption={(input, option) => {
          const label = option?.label
          const searchText =
            option?.searchText ??
            (label != null && typeof label !== 'object' ? String(label) : '')
          return searchText.toLowerCase().includes(input.toLowerCase())
        }}
        onOpenChange={isVisible => {
          if (
            isVisible &&
            !hasStaticOptions &&
            name !== 'items[].wasteManagementRoutesSource' &&
            _.get(state.options, [projectId, name]) === undefined
          ) {
            fetchOptions(name)
          }
          if (!isVisible) {
            updateFilter(name, selected) // Ensure filter is updated when dropdown is closed, even if no change was made to the selection
          }
        }}
        getPopupContainer={trigger => document.body}
        value={selected}
        onChange={setSelected}
      />
    </div>
  )
}

const TruncatedLabel = styled.span`
  display: inline-block;
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`

RecordsSelect.propTypes = {
  name: PropTypes.string,
  state: PropTypes.object,
  fetchOptions: PropTypes.func,
  filters: PropTypes.object,
  updateFilter: PropTypes.func,
  staticOptions: PropTypes.arrayOf(PropTypes.string)
}
